import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class CreditFacilityService {

  constructor(private http: HttpClient) { }

  /**
   * Save Credit Facility data (inline grid save).
   * Matches OtherOverridesDeleteService pattern:
   *   1. Start with globalService.globalParams (or flat globalService)
   *   2. Merge filterParams
   *   3. Add clob_data, clob_settings, process_name (required by action_code cqkan4)
   *   4. Ensure required fields with fallback chains
   *   5. POST as FormData
   */
  saveData(
    baseUrl: string,
    clobData: any[] | string,
    clobSettings: any[] | string,
    processName: string,
    ssoId: string,
    globalService: any = {},
    filterParams: any = {},
    rowData: any = {}
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_code=cqkan4`;

    // 1. Start with globalParams (handle nested or flat shape)
    let params: any = {};
    if (globalService?.globalParams) {
      Object.assign(params, globalService.globalParams);
    } else {
      Object.assign(params, globalService);
    }

    // 2. Merge filterParams
    Object.assign(params, filterParams);

    // 3. Merge row data payload (OtherOverrides-style direct field merge)
    Object.assign(params, rowData || {});
    // Never send wrapper keys from grid payloads
    delete params.data;
    delete params.row_id;

    // 4. Add clob_data, clob_settings, process_name (required by this action code)
    params.clob_data = typeof clobData === 'string' ? clobData : JSON.stringify(clobData);
    params.clob_settings = typeof clobSettings === 'string' ? clobSettings : JSON.stringify(clobSettings);
    params.process_name = processName;

    // 5. Ensure required fields with fallback chains (matching OtherOverridesDeleteService)
    params.tax_year = params.tax_year || filterParams?.tax_year || globalService?.tax_year || globalService?.globalParams?.tax_year || '';
    params.scenario = params.scenario || filterParams?.scenario || globalService?.scenario || globalService?.globalParams?.scenario || '';
    params.jcd_key = params.jcd_key || filterParams?.jcd_key || globalService?.jcd_key || globalService?.globalParams?.jcd_key || '';
    params.scenarioCode = params.scenarioCode || filterParams?.scenarioCode || filterParams?.scenario_code || globalService?.scenarioCode || globalService?.globalParams?.scenarioCode || 'RAF';
    params.is_issue_key_enabled = params.is_issue_key_enabled || filterParams?.is_issue_key_enabled || globalService?.is_issue_key_enabled || globalService?.globalParams?.is_issue_key_enabled || 'N';

    // 6. Add required parameters
    params.sso_id = ssoId;

    // 7. Build FormData and POST (matching OtherOverridesDeleteService)
    const formData = new FormData();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        formData.append(key, params[key].toString());
      }
    });

    return this.http.post<any>(url, formData, { withCredentials: true });
  }

  /**
   * Delete Credit Facility data.
   * Matches OtherOverridesDeleteService pattern:
   *   1. Start with globalService.globalParams (or flat globalService)
   *   2. Merge filterParams
   *   3. Add clob_data, clob_settings, process_name (required by action_code f2bj5h)
   *   4. Ensure required fields with fallback chains
   *   5. POST as FormData
   */
  deleteData(
    baseUrl: string,
    clobData: any[] | string,
    clobSettings: any[] | string,
    processName: string,
    ssoId: string,
    globalService: any = {},
    filterParams: any = {},
    rowData: any = {}
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_code=f2bj5h`;

    // 1. Start with globalParams (handle nested or flat shape)
    let params: any = {};
    if (globalService?.globalParams) {
      Object.assign(params, globalService.globalParams);
    } else {
      Object.assign(params, globalService);
    }

    // 2. Merge filterParams
    Object.assign(params, filterParams);

    // 3. Merge row data payload (OtherOverrides-style direct field merge)
    Object.assign(params, rowData || {});
    // Never send wrapper keys from grid payloads
    delete params.data;
    delete params.row_id;

    // 4. Add clob_data, clob_settings, process_name (required by this action code)
    params.clob_data = typeof clobData === 'string' ? clobData : JSON.stringify(clobData);
    params.clob_settings = typeof clobSettings === 'string' ? clobSettings : JSON.stringify(clobSettings);
    params.process_name = processName;

    // 5. Ensure required fields with fallback chains (matching OtherOverridesDeleteService)
    params.tax_year = params.tax_year || filterParams?.tax_year || globalService?.tax_year || globalService?.globalParams?.tax_year || '';
    params.scenario = params.scenario || filterParams?.scenario || globalService?.scenario || globalService?.globalParams?.scenario || '';
    params.jcd_key = params.jcd_key || filterParams?.jcd_key || globalService?.jcd_key || globalService?.globalParams?.jcd_key || '';
    params.scenarioCode = params.scenarioCode || filterParams?.scenarioCode || filterParams?.scenario_code || globalService?.scenarioCode || globalService?.globalParams?.scenarioCode || 'RAF';
    params.is_issue_key_enabled = params.is_issue_key_enabled || filterParams?.is_issue_key_enabled || globalService?.is_issue_key_enabled || globalService?.globalParams?.is_issue_key_enabled || 'N';

    // 6. Add required parameters
    params.sso_id = ssoId;

    // 7. Build FormData and POST (matching OtherOverridesDeleteService)
    const formData = new FormData();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        formData.append(key, params[key].toString());
      }
    });

    return this.http.post<any>(url, formData, { withCredentials: true });
  }

  /**
   * Generate PDF via bulk-pdf-engine/form/generatePdf.
   * POST JSON body (matches Bruno / bulk-pdf-engine contract).
   */
  generatePdf(
    pdfEngineUrl: string,
    pdfPayload: {
      taxYear: string;
      scenario: string;
      jcdKey: string;
      leType: string;
      selectedView: string | null;
      form: string;
    }
  ): Observable<any> {
    const url = `${pdfEngineUrl}/form/generatePdf`;
    return this.http.post(url, pdfPayload, {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
      withCredentials: true,
      responseType: 'text'
    }).pipe(
      map((body: string) => this.parsePdfResponseBody(body))
    );
  }

  parsePdfResponseBody(body: any): any {
    if (body == null || body === '') {
      return {};
    }
    if (typeof body === 'string') {
      try {
        return JSON.parse(body);
      } catch {
        return { message: body };
      }
    }
    return body;
  }

  getPdfErrorMessage(response: any): string | null {
    const errorMessage = response?.errorMessage;
    if (errorMessage && String(errorMessage) !== 'null' && String(errorMessage).trim()) {
      return String(errorMessage);
    }
    if (response?.callSuccess === '0' || response?.callSuccess === 0) {
      const message = response?.message;
      return message && String(message).trim()
        ? String(message)
        : 'PDF generation failed.';
    }
    return null;
  }

  getPdfSuccessMessage(response: any, fallback: string): string {
    const message = response?.message || response?.successMessage;
    return message && String(message).trim() ? String(message) : fallback;
  }

  getPdfHttpErrorMessage(error: any, fallback: string): string {
    const parsed = this.parsePdfResponseBody(error?.error);
    return this.getPdfErrorMessage(parsed) || parsed?.message || fallback;
  }
}
